import { startOfToday } from 'date-fns';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { HEADER_ID } from '@/components/templates/PageLayout';
import { ReactSingleSelectInput, SelectForm } from '@/components/uiParts';
import { DateDomainService } from '@/domain/services';
import { useTargetsDomHeight } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { errorHandler } from '@/lib/ErrorHandler';
import { HttpError } from '@/lib/exception';
import { LocalStorageHandler } from '@/lib/LocalStorageHandler';
import { nullToEmptyString } from '@/lib/util';
import {
  buildBrandMenus,
  DAY_OF_WEEK,
  DayOfWeekPeriod,
  HandleChangeTime,
} from '@/pages/menu-managements/business-hours/service';
import {
  MenuFormError,
  validateMemo,
  validateTitleJa,
} from '@/pages/menu-managements/business-hours/validate';
import { BUTTON_AREA_ID } from '@/pages/menu-managements/components/TopButtonArea';
import { routes } from '@/router';
import { useAssociateBrandList } from '@/services/AssociateBrand/hooks/list/useAssociateBrandList';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import { useMenuManagementCreateBrandMenuHour } from '@/services/BrandMenuHour/hooks/create/useMenuManagementCreateBrandMenuHour';
import { useMenuManagementBrandMenuHourShow } from '@/services/BrandMenuHour/hooks/show/useMenuManagementBrandMenuHourShow';
import { useMenuManagementUpdateBrandMenuHour } from '@/services/BrandMenuHour/hooks/update/useMenuManagementUpdateBrandMenuHour';

type Return = {
  associateBrandSelectInput: ReactSingleSelectInput;
  titleJa: string;
  memo: string;
  error: MenuFormError;
  targetsHeight: number;
  initialStringifyFormState: string;
  dayOfWeekPeriods: DayOfWeekPeriod[];
  handleAddMenuHour: (index: number) => void;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  handleDeleteTime: (argDayOfWeekIndex: number, argPeriodIndex: number) => void;
  handleChangeStartTimeHour: HandleChangeTime;
  handleChangeStartTimeMinutes: HandleChangeTime;
  handleChangeEndTimeHour: HandleChangeTime;
  handleChangeEndTimeMinutes: HandleChangeTime;
  handleCreate: () => void;
  handleUpdate: (urlParamsId: string) => void;
  handleInputTitleJa: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputMemo: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  initializeNewPageState: () => Promise<void>;
  initializeEditPageState: (urlParamsId: string) => Promise<void>;
};

const VALIDATE_MESSAGE =
  '開始時間は終了時間を超える時間に設定することは出来ません。';

export const useMenuManagementBusinessHourForm = (): Return => {
  const history = useHistory();
  const { managerAccountMe } = useManagerAccountMe();
  /* アクセス制御 */
  redirect404Page('menu_management', history, managerAccountMe);
  const { formatHHmm, formatDay } = DateDomainService;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [titleJa, setTitleJa] = useState<string>('');
  const errorObj = { message: '', isError: false, isTouched: false };
  const [error, setError] = useState({
    titleJa: { ...errorObj },
    memo: { ...errorObj },
  });
  const [associateBrandSelectOptions, setAssociateBrandSelectOptions] =
    useState<SelectForm[]>([]);
  const [brandSelect, setBrandSelect] = useState<SelectForm | null>({
    label: '',
    value: '',
  });
  const [dayOfWeekPeriods, setDayOfWeekPeriods] = useState<DayOfWeekPeriod[]>(
    []
  );
  const [memo, setMemo] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [initialStringifyFormState, setInitialStringifyFormState] =
    useState<string>('');

  const handleInputTitleJa = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitleJa = e.target.value;
    const newError = validateTitleJa(newTitleJa, error);
    setTitleJa(newTitleJa), setError(newError);
  };

  const handleInputMemo = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newMemo = e.target.value;
    const newError = validateMemo(newMemo, error);
    setMemo(newMemo), setError(newError);
  };

  const handleAddMenuHour = (index: number) => {
    const copyDayOfWeekPeriods = [...dayOfWeekPeriods];
    const formatStr = formatDay(startOfToday());
    const start_time = new Date(`${formatStr} 10:00`);
    const end_time = new Date(`${formatStr} 20:00`);
    copyDayOfWeekPeriods[index].periods.push({ start_time, end_time });
    setDayOfWeekPeriods(copyDayOfWeekPeriods);
  };

  const validateCompareTime = (startTime: Date, endTime: Date) => {
    return startTime.getTime() >= endTime.getTime();
  };

  const handleChangeStartTimeHour = (
    hour: string,
    dayOfWeekIndex: number,
    periodIndex: number
  ) => {
    const copyDayOfWeekPeriods = [...dayOfWeekPeriods];
    const periodDate =
      copyDayOfWeekPeriods[dayOfWeekIndex].periods[periodIndex];
    const startTime = periodDate.start_time;
    const endTime = periodDate.end_time;
    const newStartTime = new Date(startTime.getTime());
    newStartTime.setHours(Number(hour));
    if (validateCompareTime(newStartTime, endTime)) {
      alert(VALIDATE_MESSAGE);
      return;
    }
    copyDayOfWeekPeriods[dayOfWeekIndex].periods[periodIndex].start_time =
      newStartTime;
    setDayOfWeekPeriods(copyDayOfWeekPeriods);
  };

  const handleChangeStartTimeMinutes = (
    minutes: string,
    dayOfWeekIndex: number,
    periodIndex: number
  ) => {
    const copyDayOfWeekPeriods = [...dayOfWeekPeriods];
    const periodDate =
      copyDayOfWeekPeriods[dayOfWeekIndex].periods[periodIndex];
    const startTime = periodDate.start_time;
    const endTime = periodDate.end_time;
    const newStartTime = new Date(startTime.getTime());
    newStartTime.setMinutes(Number(minutes));
    if (validateCompareTime(newStartTime, endTime)) {
      alert(VALIDATE_MESSAGE);
      return;
    }
    copyDayOfWeekPeriods[dayOfWeekIndex].periods[periodIndex].start_time =
      newStartTime;
    setDayOfWeekPeriods(copyDayOfWeekPeriods);
  };

  const handleChangeEndTimeHour = (
    hour: string,
    dayOfWeekIndex: number,
    periodIndex: number
  ) => {
    const copyDayOfWeekPeriods = [...dayOfWeekPeriods];
    const periodDate =
      copyDayOfWeekPeriods[dayOfWeekIndex].periods[periodIndex];
    const startTime = periodDate.start_time;
    const endTime = periodDate.end_time;
    const newEndTime = new Date(endTime.getTime());
    newEndTime.setHours(Number(hour));
    if (validateCompareTime(startTime, newEndTime)) {
      alert(VALIDATE_MESSAGE);
      return;
    }
    copyDayOfWeekPeriods[dayOfWeekIndex].periods[periodIndex].end_time =
      newEndTime;
    setDayOfWeekPeriods(copyDayOfWeekPeriods);
  };

  const handleChangeEndTimeMinutes = (
    hour: string,
    dayOfWeekIndex: number,
    periodIndex: number
  ) => {
    const copyDayOfWeekPeriods = [...dayOfWeekPeriods];
    const periodDate =
      copyDayOfWeekPeriods[dayOfWeekIndex].periods[periodIndex];
    const startTime = periodDate.start_time;
    const endTime = periodDate.end_time;
    const newEndTime = new Date(endTime.getTime());
    newEndTime.setMinutes(Number(hour));
    if (validateCompareTime(startTime, newEndTime)) {
      alert(VALIDATE_MESSAGE);
      return;
    }
    copyDayOfWeekPeriods[dayOfWeekIndex].periods[periodIndex].end_time =
      newEndTime;
    setDayOfWeekPeriods(copyDayOfWeekPeriods);
  };

  const handleDeleteTime = (
    argDayOfWeekIndex: number,
    argPeriodIndex: number
  ) => {
    const copyDayOfWeekPeriods = [...dayOfWeekPeriods];
    const filterdDayOfWeekPeriods = copyDayOfWeekPeriods.map(
      (dayOfWeek, dayOfWeekIndex) => {
        if (argDayOfWeekIndex === dayOfWeekIndex) {
          return {
            ...dayOfWeek,
            periods: dayOfWeek.periods.filter((period, periodIndex) => {
              return argPeriodIndex !== periodIndex;
            }),
          };
        } else {
          return dayOfWeek;
        }
      }
    );
    setDayOfWeekPeriods(filterdDayOfWeekPeriods);
  };

  const associateBrandSelectInput = {
    value: brandSelect,
    options: associateBrandSelectOptions,
    onChange: (newBrand: SelectForm | null) => {
      if (newBrand?.value === brandSelect?.value) return;
      setBrandSelect(newBrand);
    },
    onReset: () => {
      setBrandSelect({
        value: '',
        label: '',
      });
    },
  };

  const listUrl = routes.menuManagementBusinessHours.path;

  const buildDayOfWeekPeriods = dayOfWeekPeriods
    .filter((dowp) => dowp.periods.length > 0)
    .map((dowp) => {
      return {
        ...dowp,
        periods: dowp.periods.map((period) => {
          return {
            start_time: formatHHmm(period.start_time),
            end_time: formatHHmm(period.end_time),
          };
        }),
      };
    });

  const handleCreate = () => {
    (async () => {
      setIsLoading(true);
      await useMenuManagementCreateBrandMenuHour({
        associate_brand_id: Number(associateBrandSelectInput.value?.value),
        title: { translation: { ja: titleJa } },
        memo: memo !== '' ? memo : null,
        day_of_week_periods: buildDayOfWeekPeriods,
      });
      setIsLoading(false);
      alert('メニュー提供時間を登録しました。');
      history.push(listUrl);
    })();
  };

  const handleUpdate = (urlParamsId: string) => {
    (async () => {
      try {
        setIsLoading(true);
        await useMenuManagementUpdateBrandMenuHour(urlParamsId, {
          associate_brand_id: Number(associateBrandSelectInput.value?.value),
          title: {
            translation: {
              ja: titleJa,
            },
          },
          memo: memo !== '' ? memo : null,
          day_of_week_periods: buildDayOfWeekPeriods,
        });
        setIsLoading(false);
        alert('メニュー提供時間を更新しました。');
        history.push(listUrl);
      } catch (error) {
        setIsLoading(false);
        if (error instanceof HttpError && error.status === 400) {
          return alert(error.message);
        }
        // @ts-ignore TS2322
        errorHandler({ error });
      }
    })();
  };

  const { targetsHeight } = useTargetsDomHeight([HEADER_ID, BUTTON_AREA_ID]);

  const initializeNewPageState = async () => {
    const associateBrands = await useAssociateBrandList();
    const associateBrandOptions = associateBrands.map((brand) => {
      return {
        label: brand.name,
        value: brand.id.toString(),
      };
    });
    const { associateBrand } = LocalStorageHandler.get('menuManagement');
    const initialAssociateBrand =
      associateBrand !== undefined ? associateBrand : associateBrandOptions[0];
    setBrandSelect(initialAssociateBrand);
    LocalStorageHandler.set('menuManagement', {
      associateBrand: initialAssociateBrand,
    });
    setAssociateBrandSelectOptions(associateBrandOptions);
    const newDayOfWeekPeriods: DayOfWeekPeriod[] = DAY_OF_WEEK.map(
      (dayOfWeek) => {
        return {
          day_of_week: dayOfWeek,
          periods: [],
        };
      }
    );
    setDayOfWeekPeriods(newDayOfWeekPeriods);
  };

  const initializeEditPageState = async (urlParamsId: string) => {
    const { associateBrand } = LocalStorageHandler.get('menuManagement');
    if (associateBrand === undefined) return history.push(listUrl);
    const brandMenu = await useMenuManagementBrandMenuHourShow(urlParamsId);
    setBrandSelect(associateBrand);
    const newTitleJa = brandMenu.title.translation.ja;
    const newMemo = nullToEmptyString(brandMenu.memo);
    const newDayOfWeekPeriods = buildBrandMenus(brandMenu.day_of_week_periods);
    setDayOfWeekPeriods(newDayOfWeekPeriods);
    setTitleJa(newTitleJa);
    setMemo(newMemo);
    setInitialStringifyFormState(
      JSON.stringify([newTitleJa, newMemo, newDayOfWeekPeriods])
    ); // WARNING 文字列比較なので順番を変更するときは注意
  };

  return {
    associateBrandSelectInput,
    titleJa,
    memo,
    error,
    targetsHeight,
    initialStringifyFormState,
    dayOfWeekPeriods,
    isLoading,
    setIsLoading,
    handleAddMenuHour,
    handleDeleteTime,
    handleChangeStartTimeHour,
    handleChangeStartTimeMinutes,
    handleChangeEndTimeHour,
    handleChangeEndTimeMinutes,
    handleCreate,
    handleUpdate,
    handleInputTitleJa,
    handleInputMemo,
    initializeNewPageState,
    initializeEditPageState,
  };
};
